import React, { useState, useEffect } from 'react';
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Button from "@mui/material/Button";
import {Modal, IconButton} from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import RateReviewIcon from '@mui/icons-material/RateReview';
import InfoIcon from '@mui/icons-material/Info';
import {Testimonial} from "../../../models/site";
import {fetchTestimonialsPaginated, deleteTestimonial} from "../../../components/business_logic/testimonial_requests";
import PreviewTestimonial from "./preview_testimonial";
import NewTestimonial from "./new_testimonial";
import DeleteItemDialog from "../templates/delete_item_dialog";
import CircularProgress from "@mui/material/CircularProgress";
import Rating from "@mui/material/Rating";

export default function AccountTestimonial() {
    const [testimonial, setTestimonial] = useState<Testimonial | null>(null);
    const [testimonialToDelete, setTestimonialToDelete] = useState<Testimonial | null>(null);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [newTestimonialOpen, setNewTestimonialOpen] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadTestimonial().then();
    }, []);

    const loadTestimonial = async () => {
        setLoading(true);
        try {
            const testimonials = await fetchTestimonialsPaginated(1);
            // User can only have one testimonial, so take the first one if it exists
            if (testimonials.length > 0) {
                setTestimonial(testimonials[0]);
            } else {
                setTestimonial(null);
            }
        } catch (error) {
            console.error('Failed to load testimonial:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleTestimonialClick = () => {
        if (testimonial) {
            setPreviewOpen(true);
        }
    };

    const handleClosePreview = () => {
        setPreviewOpen(false);
    };

    const handleOpenNewTestimonial = (edit: boolean = false) => {
        setIsEditMode(edit);
        setNewTestimonialOpen(true);
    };

    const handleCloseNewTestimonial = () => {
        setNewTestimonialOpen(false);
        setIsEditMode(false);
    };

    const handleTestimonialCreated = () => {
        loadTestimonial().then();
    };

    const handleDeleteClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (testimonial) {
            setTestimonialToDelete(testimonial);
        }
    };

    const formatDate = (dateString?: string) => {
        if (!dateString) return 'N/A';
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            });
        } catch {
            return dateString;
        }
    };


    if (loading) {
        return (
            <Container sx={{ py: 4, textAlign: 'center' }}>
                <CircularProgress />
                <Typography sx={{ mt: 2 }}>Loading your testimonial...</Typography>
            </Container>
        );
    }

    return (
        <Container sx={{ py: 4 }}>
            {/* Header Section */}
            <Box sx={{ mb: 4 }}>
                <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    mb: 3,
                    flexWrap: 'wrap',
                    gap: 2
                }}>
                    <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
                        My Testimonial
                    </Typography>
                    {!testimonial && (
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => handleOpenNewTestimonial(false)}
                            size="large"
                        >
                            Create Testimonial
                        </Button>
                    )}
                </Box>

                {/* Information Section */}
                <Card variant="outlined" sx={{ mb: 4, backgroundColor: 'action.hover' }}>
                    <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                            <InfoIcon sx={{ color: 'primary.main', mt: 0.5 }} />
                            <Box>
                                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                                    Share Your Experience
                                </Typography>
                                <Typography variant="body2" color="text.secondary" paragraph>
                                    Help others discover PennyPusher by sharing your experience. 
                                    Your testimonial will be featured on our public testimonials page.
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    <strong>Note:</strong> You can only create one testimonial. You can update or delete it at any time.
                                </Typography>
                            </Box>
                        </Box>
                    </CardContent>
                </Card>
            </Box>

            {/* Testimonial Section */}
            {!testimonial ? (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                    <RateReviewIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                        No Testimonial Yet
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Share your experience with PennyPusher by creating your first testimonial.
                    </Typography>
                    <Button
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={() => handleOpenNewTestimonial(false)}
                    >
                        Create Testimonial
                    </Button>
                </Box>
            ) : (
                <>
                    <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                        Your Testimonial
                    </Typography>
                    <Grid container spacing={3}>
                        <Grid size={{ xs: 12, md: 8 }}>
                            <Card
                                variant="outlined"
                                sx={{
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease-in-out',
                                    '&:hover': {
                                        boxShadow: 4,
                                        transform: 'translateY(-2px)',
                                    },
                                    borderColor: 'divider',
                                }}
                                onClick={handleTestimonialClick}
                            >
                                <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                        <Typography 
                                            variant="h6" 
                                            component="h2" 
                                            sx={{ 
                                                fontWeight: 600,
                                                flex: 1,
                                                mr: 1
                                            }}
                                        >
                                            {testimonial.title || 'Untitled Testimonial'}
                                        </Typography>
                                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                            <IconButton
                                                size="small"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleOpenNewTestimonial(true);
                                                }}
                                                sx={{
                                                    color: 'primary.main',
                                                    '&:hover': {
                                                        backgroundColor: 'primary.light',
                                                        color: 'primary.dark',
                                                    },
                                                }}
                                            >
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                onClick={handleDeleteClick}
                                                sx={{
                                                    color: 'error.main',
                                                    '&:hover': {
                                                        backgroundColor: 'error.light',
                                                        color: 'error.dark',
                                                    },
                                                }}
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    </Box>
                                    
                                    {testimonial.rating && (
                                        <Box sx={{ mb: 2 }}>
                                            <Rating 
                                                value={testimonial.rating} 
                                                readOnly 
                                                size="small"
                                                sx={{
                                                    '& .MuiRating-iconFilled': {
                                                        color: 'primary.main'
                                                    }
                                                }}
                                            />
                                        </Box>
                                    )}

                                    <Box sx={{ 
                                        display: 'flex', 
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        mt: 'auto',
                                        pt: 2,
                                        borderTop: '1px solid',
                                        borderColor: 'divider'
                                    }}>
                                        <Typography variant="caption" color="text.secondary">
                                            {formatDate(testimonial.updated_at || testimonial.created_at)}
                                        </Typography>
                                        <Typography variant="caption" color="primary">
                                            View details →
                                        </Typography>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                </>
            )}

            {/* Preview Modal */}
            <Modal 
                open={previewOpen && !!testimonial}
                onClose={(event, reason) => {
                    if (reason !== 'backdropClick') {
                        handleClosePreview();
                    }
                }}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 2,
                }}
            >
                <Box 
                    sx={{
                        position: 'relative',
                        width: "90%",
                        maxWidth: "900px",
                        maxHeight: "90vh",
                        backgroundColor: 'background.paper',
                        borderRadius: 2,
                        boxShadow: 24,
                        outline: 'none',
                        overflow: 'auto',
                        border: '1px solid',
                        borderColor: 'divider',
                    }}
                >
                    <IconButton
                        onClick={handleClosePreview}
                        sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            backgroundColor: 'rgba(0, 0, 0, 0.5)',
                            color: 'white',
                            zIndex: 1,
                            '&:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.7)',
                            },
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                    {testimonial && <PreviewTestimonial testimonial={testimonial} isAdmin={false} />}
                </Box>
            </Modal>

            {/* New/Edit Testimonial Modal */}
            <NewTestimonial 
                open={newTestimonialOpen}
                onClose={handleCloseNewTestimonial}
                onSuccess={handleTestimonialCreated}
                existingTestimonial={isEditMode && testimonial ? testimonial : undefined}
            />

            {/* Delete Confirmation Dialog */}
            <DeleteItemDialog
                itemToDelete={testimonialToDelete}
                setItemToDelete={setTestimonialToDelete}
                onDelete={() => deleteTestimonial(testimonialToDelete!)}
                onSuccess={() => {
                    loadTestimonial().then();
                }}
                itemName={(t) => t.title || 'Untitled Testimonial'}
                itemDetails={(t) => (
                    <>
                        <Typography variant="body2" color="text.secondary">
                            ID: {t.id}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Created: {formatDate(t.created_at)}
                        </Typography>
                        {t.rating && (
                            <Typography variant="body2" color="text.secondary">
                                Rating: {t.rating} / 5
                            </Typography>
                        )}
                    </>
                )}
                title="Delete Testimonial"
                description="Are you sure you want to delete your testimonial?"
                warningMessage="⚠️ This action cannot be undone and will permanently delete your testimonial."
                requireNameVerification={false}
            />
        </Container>
    );
}